import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import useENS from 'hooks/useENS'
import { AlertTriangle, Edit, X } from 'react-feather'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { isAddress } from 'utils'
import { shortenAddress } from 'utils'

const CollapsedContainer = styled.div`
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  font-size: 13px;
  align-items: center;
  justify-content: space-between;
`

const WarningIcon = styled(AlertTriangle)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.accentWarning};
  margin-right: 6px;
`

const AddressText = styled(ThemedText.BodySmall)`
  font-family: monospace;
  color: ${({ theme }) => theme.textPrimary};
`

const LeftContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  flex: 1;

  &:hover {
    opacity: 0.8;
  }
`

const RightContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 4px 0 4px 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 4px 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

interface ReceiverCollapsedProps {
  recipient: string
  onClick: () => void
  onRemove: () => void
  showWarning?: boolean
}

export default function ReceiverCollapsed({
  recipient,
  onClick,
  onRemove,
  showWarning = true,
}: ReceiverCollapsedProps) {
  const { account } = useWeb3React()

  // Only call useENS if recipient is not empty and not already an address
  const shouldUseENS = recipient && recipient.length > 0 && !isAddress(recipient)
  const { name, address } = useENS(shouldUseENS ? recipient : undefined)

  const isDifferent = showWarning && account && recipient.toLowerCase() !== account.toLowerCase()

  // If ENS name exists, show both name and shortened address
  const displayAddress = name ? `${name} (${shortenAddress(address || recipient)})` : shortenAddress(recipient)

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRemove()
  }

  return (
    <CollapsedContainer>
      <LeftContent onClick={onClick}>
        {isDifferent && <WarningIcon />}
        <ThemedText.BodySmall color="textSecondary">
          <Trans>Recipient address:</Trans>
        </ThemedText.BodySmall>
      </LeftContent>
      <RightContent>
        <AddressText>{displayAddress}</AddressText>
        <EditButton onClick={onClick} aria-label="Edit recipient">
          <Edit size={13} />
        </EditButton>
        <RemoveButton onClick={handleRemove} aria-label="Remove recipient">
          <X size={14} />
        </RemoveButton>
      </RightContent>
    </CollapsedContainer>
  )
}
